import { Injectable, OnDestroy } from '@angular/core';
import { Firestore, doc, docData, updateDoc } from '@angular/fire/firestore';
import { Observable, Subject } from 'rxjs';
import { map, takeUntil, tap } from 'rxjs/operators';
import { FormStatus } from '~app/company/shared/model/company.model';
import { ReceiptModel } from '~app/shopping-cart/receipt/state/receipt.model';
import { AnnualDeclaration, AnnualDeclarationFormCollection } from '../model/annual-declaration.model';
import { AttachedDocuments, CompanyOtherEstablishmentFirebase } from '../model/company-identification.model';

export enum VersionType {
  Original = 'original',
  Current = 'current',
}

@Injectable({
  providedIn: 'root',
})
export class AnnualDeclarationFormFirebaseService implements OnDestroy {

  private collectionName = `legal-entity-annual-declaration`;
  private destroy$ = new Subject<void>();

  // buffer so that we can send datas as they are on Save.
  public attachedDocumentsBuffer: AttachedDocuments[] = [];

  constructor(private firestore: Firestore) {}

  // ---------------------------------------------------------------------------
  // 🔥 Firestore modular version of getCollectionDocument()
  // ---------------------------------------------------------------------------
  private getCollectionDocument(legalEntityId: string, year: number) {
    return doc(
      this.firestore,
      `${this.collectionName}/${year}/forms/${legalEntityId}`
    );
  }

  // ---------------------------------------------------------------------------
  // 🔥 getAnnualDeclarationFormCollection()
  // ---------------------------------------------------------------------------
  getAnnualDeclarationFormCollection(
    legalEntityId: string,
    year: number
  ): Observable<AnnualDeclarationFormCollection> {

    const ref = this.getCollectionDocument(legalEntityId, year);

    return docData(ref).pipe(
      tap(collection => this.attachedDocumentsBuffer = collection?.attachedDocuments),
      takeUntil(this.destroy$),
      map(data => data as AnnualDeclarationFormCollection)
    );
  }

  // ---------------------------------------------------------------------------
  // 🔥 updateCurrentAnnualDeclaration()
  // ---------------------------------------------------------------------------
  updateCurrentAnnualDeclaration(
    legalEntityId: string,
    annualDeclaration: AnnualDeclaration,
    year: number
  ): Promise<void> {

    const ref = this.getCollectionDocument(legalEntityId, year);

    return updateDoc(ref, {
      lastSavedDate: new Date().toISOString(),
      current: annualDeclaration,
      state: FormStatus.InProgress
    });
  }

  // ---------------------------------------------------------------------------
  // 🔥 updateSummaryAttachment()
  // ---------------------------------------------------------------------------
  updateSummaryAttachment(
    legalEntityId: string,
    summaryAttachment: AttachedDocuments,
    year: number
  ) {
    const ref = this.getCollectionDocument(legalEntityId, year);
    return updateDoc(ref, { summaryAttachment });
  }

  // ---------------------------------------------------------------------------
  // 🔥 updatePaymentConfirmation()
  // ---------------------------------------------------------------------------
  updatePaymentConfirmation(
    legalEntityId: string,
    paymentConfirmation: ReceiptModel,
    year: number
  ) {
    const ref = this.getCollectionDocument(legalEntityId, year);
    return updateDoc(ref, { paymentConfirmation });
  }

  // ---------------------------------------------------------------------------
  // 🔥 updateAnnualDeclarationState()
  // ---------------------------------------------------------------------------
  updateAnnualDeclarationState(
    legalEntityId: string,
    state: FormStatus,
    year: number
  ) {
    const ref = this.getCollectionDocument(legalEntityId, year);
    return updateDoc(ref, { state });
  }

  // ---------------------------------------------------------------------------
  // 🔥 getDeclarationYear()
  // ---------------------------------------------------------------------------
  getDeclarationYear(): Observable<number> {
    const ref = doc(this.firestore, `${this.collectionName}/settings`);

    return docData(ref).pipe(
      map(data => (data as any)?.year)
    );
  }

  // ---------------------------------------------------------------------------
  // 🔥 getEstablishmentMemberStatus()
  // ---------------------------------------------------------------------------
  getEstablishmentMemberStatus(
    legalEntityId: string
  ): Observable<CompanyOtherEstablishmentFirebase[]> {

    const ref = doc(this.firestore, `legal-entity-establishments/${legalEntityId}`);

    return docData(ref).pipe(
      map((doc: any) => doc?.establishments ?? [])
    );
  }

  // ---------------------------------------------------------------------------
  // 🔥 ngOnDestroy()
  // ---------------------------------------------------------------------------
  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
